import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Megaphone, TrendingUp, Award, MessageCircle, Zap, Calendar, Users } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Course, Campaign } from '../types/database'

interface DashboardProps {
  user: any
}

interface ActivityItem {
  id: string
  type: 'course' | 'campaign' | 'badge' | 'discussion'
  title: string
  description: string
  timestamp: string
  icon: any
  color: string
}

export default function Dashboard({ user }: DashboardProps) {
  const [myCourses, setMyCourses] = useState<Course[]>([])
  const [myCampaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    educationPoints: 0,
    participationPoints: 0,
    changeScore: 0
  })
  const [activityFeed, setActivityFeed] = useState<ActivityItem[]>([])
  const [badges, setBadges] = useState<any[]>([])

  useEffect(() => {
    if (!user) return
    fetchDashboardData()
  }, [user])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // Obtener perfil del usuario
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      if (profile) {
        setStats({
          educationPoints: profile.education_points || 0,
          participationPoints: profile.participation_points || 0,
          changeScore: profile.change_score || 0
        })
      }

      // Obtener cursos en progreso
      const { data: coursesData } = await supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .order('rating', { ascending: false })
        .limit(3)

      setMyCourses(coursesData || [])

      // Obtener campañas activas
      const { data: campaignsData } = await supabase
        .from('campaigns')
        .select('*')
        .eq('status', 'active')
        .eq('is_published', true)
        .order('current_signatures', { ascending: false })
        .limit(3)

      setCampaigns(campaignsData || [])

      // Obtener badges del usuario
      const { data: userBadges } = await supabase
        .from('user_badges')
        .select('*, badges(*)')
        .eq('user_id', user.id)
        .order('earned_at', { ascending: false })
        .limit(6)

      setBadges(userBadges || [])

      // Generar feed de actividad (simulado basado en datos reales)
      const feed: ActivityItem[] = []
      
      if (coursesData && coursesData.length > 0) {
        feed.push({
          id: '1',
          type: 'course',
          title: 'Progreso en curso',
          description: `Estas aprendiendo: ${coursesData[0].title}`,
          timestamp: 'Hace 2 horas',
          icon: BookOpen,
          color: 'education'
        })
      }

      if (campaignsData && campaignsData.length > 0) {
        feed.push({
          id: '2',
          type: 'campaign',
          title: 'Nueva campana disponible',
          description: `${campaignsData[0].title} necesita tu apoyo`,
          timestamp: 'Hace 1 dia',
          icon: Megaphone,
          color: 'action'
        })
      }

      feed.push({
        id: '3',
        type: 'discussion',
        title: 'Nuevos comentarios en foro',
        description: '12 nuevas respuestas en tus discusiones',
        timestamp: 'Hace 2 dias',
        icon: MessageCircle,
        color: 'community'
      })

      if (userBadges && userBadges.length > 0) {
        feed.push({
          id: '4',
          type: 'badge',
          title: 'Badge ganado',
          description: `Conseguiste: ${userBadges[0].badges?.name || 'Nuevo badge'}`,
          timestamp: 'Hace 3 dias',
          icon: Award,
          color: 'urgency'
        })
      }

      setActivityFeed(feed)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!user) return null

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-education mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Cargando tu dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-gray-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header con saludo */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-neutral-800 mb-2">
            Bienvenido de nuevo
          </h1>
          <p className="text-lg text-gray-600">Continua tu viaje de aprendizaje y accion</p>
        </div>

        {/* Progreso Híbrido Visual Mejorado */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 transform hover:scale-[1.01] transition-transform">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-xl text-gray-900">Tu Progreso Hibrido</h2>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Zap className="w-4 h-4 text-urgency" />
              <span>Racha de 7 dias</span>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center md:space-x-12 space-y-8 md:space-y-0">
            {/* Educación */}
            <div className="text-center group cursor-pointer">
              <div className="relative w-36 h-36 rounded-full border-8 border-education flex items-center justify-center mb-3 group-hover:border-education/80 transition-all">
                <div className="absolute inset-0 rounded-full bg-education/5 group-hover:bg-education/10 transition-all"></div>
                <div className="text-center z-10">
                  <div className="font-bold text-3xl text-education">{stats.educationPoints}</div>
                  <div className="text-xs text-neutral-600 font-semibold">Puntos</div>
                </div>
              </div>
              <div className="font-semibold text-gray-900">Educacion</div>
              <div className="text-sm text-gray-600">{myCourses.length} cursos activos</div>
            </div>

            {/* Change Score Central */}
            <div className="text-center group cursor-pointer">
              <div className="relative w-44 h-44 rounded-full bg-gradient-to-br from-education via-action to-community flex items-center justify-center mb-3 shadow-xl group-hover:shadow-2xl group-hover:scale-105 transition-all">
                <div className="w-40 h-40 rounded-full bg-white flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-bold text-4xl bg-gradient-to-r from-education to-action bg-clip-text text-transparent">
                      {stats.changeScore}
                    </div>
                    <div className="text-sm font-semibold text-gray-700">Change Score</div>
                  </div>
                </div>
              </div>
              <div className="font-semibold text-gray-900">Tu Impacto Total</div>
              <div className="text-sm text-gray-600">Nivel: Activista</div>
            </div>

            {/* Participación */}
            <div className="text-center group cursor-pointer">
              <div className="relative w-36 h-36 rounded-full border-8 border-action flex items-center justify-center mb-3 group-hover:border-action/80 transition-all">
                <div className="absolute inset-0 rounded-full bg-action/5 group-hover:bg-action/10 transition-all"></div>
                <div className="text-center z-10">
                  <div className="font-bold text-3xl text-action">{stats.participationPoints}</div>
                  <div className="text-xs text-neutral-600 font-semibold">Puntos</div>
                </div>
              </div>
              <div className="font-semibold text-gray-900">Participacion</div>
              <div className="text-sm text-gray-600">{myCampaigns.length} campanas activas</div>
            </div>
          </div>
        </div>

        {/* Badges Destacados */}
        {badges.length > 0 && (
          <div className="bg-gradient-to-r from-community/10 to-education/10 rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-6 h-6 text-community" />
                <h3 className="font-semibold text-xl text-gray-900">Tus Badges</h3>
              </div>
              <Link to="/perfil" className="text-sm text-education hover:underline font-semibold">
                Ver todos
              </Link>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {badges.map((userBadge) => (
                <div
                  key={userBadge.id}
                  className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-education to-community rounded-full flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {userBadge.badges?.name || 'Badge'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Layout 3-panel Mejorado */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel Izquierdo: Cursos */}
          <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg flex items-center space-x-2">
                <div className="w-10 h-10 bg-education/10 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-education" />
                </div>
                <span>Mis Cursos</span>
              </h3>
              <Link 
                to="/cursos" 
                className="text-sm text-education hover:text-education/80 font-semibold flex items-center gap-1 hover:gap-2 transition-all"
              >
                Ver todos
              </Link>
            </div>
            <div className="space-y-4">
              {myCourses.length > 0 ? (
                myCourses.map((course) => (
                  <div 
                    key={course.id} 
                    className="border border-neutral-200 rounded-xl p-4 hover:border-education hover:shadow-md transition-all cursor-pointer group"
                  >
                    <h4 className="font-medium text-sm mb-2 group-hover:text-education transition-colors">
                      {course.title}
                    </h4>
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 mb-2">
                      <div 
                        className="bg-gradient-to-r from-education to-education/80 h-2.5 rounded-full transition-all" 
                        style={{ width: '60%' }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">60% completado</span>
                      <span className="text-education font-semibold">{course.rating} ★</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">No tienes cursos activos</p>
                  <Link 
                    to="/cursos"
                    className="inline-block px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90 transition-colors text-sm font-semibold"
                  >
                    Explorar cursos
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Panel Central: Feed de Actividad Mejorado */}
          <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg flex items-center space-x-2">
                <div className="w-10 h-10 bg-community/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-community" />
                </div>
                <span>Actividad Reciente</span>
              </h3>
            </div>
            <div className="space-y-4">
              {activityFeed.map((item) => {
                const Icon = item.icon
                return (
                  <div 
                    key={item.id} 
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className={`w-10 h-10 bg-${item.color}/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-5 h-5 text-${item.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-education transition-colors">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-600 mb-1">{item.description}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{item.timestamp}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Panel Derecho: Campañas */}
          <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg flex items-center space-x-2">
                <div className="w-10 h-10 bg-action/10 rounded-lg flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-action" />
                </div>
                <span>Campanas Activas</span>
              </h3>
              <Link 
                to="/campanas" 
                className="text-sm text-action hover:text-action/80 font-semibold flex items-center gap-1 hover:gap-2 transition-all"
              >
                Ver todas
              </Link>
            </div>
            <div className="space-y-4">
              {myCampaigns.length > 0 ? (
                myCampaigns.map((campaign) => {
                  const progress = (campaign.current_signatures / campaign.target_signatures) * 100
                  return (
                    <div 
                      key={campaign.id} 
                      className="border border-neutral-200 rounded-xl p-4 hover:border-action hover:shadow-md transition-all cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-sm flex-1 group-hover:text-action transition-colors">
                          {campaign.title}
                        </h4>
                        {campaign.urgency_level === 'urgent' && (
                          <span className="text-xs bg-urgency text-white px-2 py-1 rounded-full font-semibold">
                            URGENTE
                          </span>
                        )}
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2.5 mb-2">
                        <div
                          className="bg-gradient-to-r from-action to-action/80 h-2.5 rounded-full transition-all"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-neutral-600">
                          <Users className="w-3 h-3" />
                          <span>
                            {campaign.current_signatures.toLocaleString()} / {campaign.target_signatures.toLocaleString()}
                          </span>
                        </div>
                        <span className="text-action font-semibold">{progress.toFixed(0)}%</span>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-8">
                  <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">No participas en campanas aun</p>
                  <Link 
                    to="/campanas"
                    className="inline-block px-4 py-2 bg-action text-white rounded-lg hover:bg-action/90 transition-colors text-sm font-semibold"
                  >
                    Explorar campanas
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sección de Recomendaciones */}
        <div className="mt-8 bg-gradient-to-r from-education/5 to-action/5 rounded-2xl shadow-lg p-8">
          <h3 className="font-semibold text-xl text-gray-900 mb-4">Recomendado para ti</h3>
          <p className="text-gray-600 mb-6">
            Basado en tu actividad reciente, estos cursos y campanas podrian interesarte
          </p>
          <div className="flex gap-4 flex-wrap">
            <Link
              to="/cursos"
              className="px-6 py-3 bg-education text-white rounded-lg hover:bg-education/90 transition-colors font-semibold"
            >
              Ver cursos recomendados
            </Link>
            <Link
              to="/campanas"
              className="px-6 py-3 bg-white text-action border-2 border-action rounded-lg hover:bg-action hover:text-white transition-colors font-semibold"
            >
              Ver campanas sugeridas
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
